import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { MonthYearPicker, LoadingSpinner, Pagination, Button, confirmAlert } from 'components/common';
import { useDispatch, useSelector } from 'react-redux';
import { AgGridReact } from 'ag-grid-react';
import { MdAssignment, MdApartment, MdNumbers, MdScale } from 'react-icons/md';
import { Link } from 'react-router-dom';
import dayjs from 'dayjs';
import {
  getPlanningForecast,
  setPlanningReportPage,
  setPlanningReportPageSize,
  releasePlanningToProduction,
  exportPlanningForecast,
  requestPlanningForecastMaterials,
  recalculatePlanningFromDocuments
} from 'redux/crm/releaseForecastSlice';
import { formatNumber, escapeHtml } from 'helpers/utils';
import { FaSearch, FaTimes } from 'react-icons/fa';
import { toast } from 'react-toastify';
import ProductionReleaseConfirmDialog from '../../../components/app/crm/release-forecast/planning/ProductionReleaseConfirmDialog';
import MaterialRequestDialog from '../../../components/app/crm/release-forecast/planning/MaterialRequestDialog';
import PlanningToolbarActions from '../../../components/app/crm/release-forecast/planning/PlanningToolbarActions';
import useDebouncedForecastSearch from '../../../components/app/crm/release-forecast/components/useDebouncedForecastSearch';
import MaterialStatusSelect from '../../../components/app/crm/release-forecast/components/MaterialStatusSelect';
import MaterialStatusBadge from '../../../components/app/crm/release-forecast/components/MaterialStatusBadge';
import { earliestReleaseLabel } from '../../../components/app/crm/release-forecast/components/helpers';

const gridStyles = `
  .ag-header-cell[col-id="metric"] {
    background-color: #fef3c7 !important;
  }
  .ag-header-cell[col-id="steel_plate"] {
    background-color: #dbeafe !important;
  }
  .ag-header-cell[col-id="square_tube"] {
    background-color: #dcfce7 !important;
  }
  .ag-header-cell[col-id="hgi"] {
    background-color: #f3e8ff !important;
  }
  .ag-header-cell-menu-button {
    display: none !important;
  }
  .ag-row[row-type="data"] {
    border-bottom: 1px solid #e5e7eb;
  }
`;

const getDefaultPeriod = () => {
  const p = new URLSearchParams(window.location.search);
  return p.get('period') || dayjs().format('MMM-YYYY');
};

const ReleaseForecastPlanningPage = () => {
  const dispatch = useDispatch();
  const gridRef = useRef(null);

  const [pageReady, setPageReady] = useState(false);
  const [selectedCsdIds, setSelectedCsdIds] = useState([]);
  const [materialRequestOpen, setMaterialRequestOpen] = useState(false);
  const reportState   = useSelector((s) => s.crm_release_forecast.planningReportData);
  const exportLoading = useSelector((s) => s.crm_release_forecast.exportLoading);

  // Memoized so the `|| []` fallback does not hand a brand-new array to the hooks
  // that depend on it (grouping, column building, the recalculate confirmation)
  // on every render.
  const rows = useMemo(() => reportState.allData?.rows || [], [reportState.allData?.rows]);
  const totalRows     = reportState.allData?.total_rows || 0;
  const totalProjects = reportState.allData?.total_projects || 0;
  const loading       = reportState.loading;
  const page          = reportState.page || 1;
  const perPage       = reportState.per_page || 50;

  const steelSpecs = useMemo(() => reportState.allData?.steel_specs || [], [reportState.allData?.steel_specs]);
  const tubeSpecs  = useMemo(() => reportState.allData?.tube_specs  || [], [reportState.allData?.tube_specs]);
  const hgiSpecs   = useMemo(() => reportState.allData?.hgi_specs   || [], [reportState.allData?.hgi_specs]);

  const [viewMode, setViewMode] = useState('project'); // 'project' | 'building'
  const [productionReleaseTarget, setProductionReleaseTarget] = useState(null);
  const unsentCount = rows.filter((r) => !r.released_to_production_at).length;

  // Projects still waiting to go to production, each with its buildings — listed
  // in the release dialog so the sender sees exactly what the email covers.
  // Rows are one building per release month, so buildings are merged by name and
  // their release months collected.
  const pendingProjects = useMemo(() => {
    const byProject = new Map();

    rows
      .filter((row) => !row._is_group && !row._is_subtotal && !row._is_footer && !row.released_to_production_at)
      .forEach((row) => {
        const projectKey  = row.csd_id || `${row.job_no}||${row.project_name}`;
        const buildingKey = row.building_name || row.id || projectKey;
        const project = byProject.get(projectKey) ||
          { id: projectKey, jobNo: row.job_no, projectName: row.project_name, buildings: new Map() };
        const building = project.buildings.get(buildingKey) ||
          { id: `${projectKey}||${buildingKey}`, name: row.building_name, releaseMonths: [] };
        const month = row.expected_finish || row.release_month;

        byProject.set(projectKey, {
          ...project,
          buildings: new Map(project.buildings).set(buildingKey, {
            ...building,
            releaseMonths: month && !building.releaseMonths.includes(month)
              ? [...building.releaseMonths, month]
              : building.releaseMonths
          })
        });
      });

    return [...byProject.values()].map((project) => ({
      ...project,
      buildings: [...project.buildings.values()]
    }));
  }, [rows]);

  const [filters, setFilters] = useState({
    period: getDefaultPeriod(),
    material_status: ''
  });
  const { searchInput, setSearchInput, searchQuery, clearSearch } = useDebouncedForecastSearch();

  const buildParams = useCallback(
    (pg = page) => {
      const p = { page: pg, per_page: perPage };
      if (filters.period) p.period = filters.period;
      if (searchQuery)             p.q = searchQuery;
      if (filters.material_status) p.material_status = filters.material_status;
      return p;
    },
    [page, perPage, filters, searchQuery]
  );

  const reload = useCallback(
    (pg = page) => dispatch(getPlanningForecast({ params: buildParams(pg) })),
    [dispatch, buildParams, page]
  );

  useEffect(() => {
    reload(1);
    // only on filter/search change: reload also changes with page, and page changes are handled below
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, searchQuery]);

  useEffect(() => {
    const styleId = 'planning-report-grid-styles';
    if (!document.getElementById(styleId)) {
      const style = document.createElement('style');
      style.id = styleId;
      style.textContent = gridStyles;
      document.head.appendChild(style);
    }
  }, []);

  const isFirst = useRef(true);
  useEffect(() => {
    if (isFirst.current) { isFirst.current = false; return; }
    reload();
    // only on page/size change: reload also changes with filters, whose effect above already reloads page 1
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, perPage]);

  const setFilter = (key, val) => setFilters((prev) => ({ ...prev, [key]: val }));
  const hasFilters = searchInput !== '' || filters.material_status !== '';
  const clearFilters = () => {
    clearSearch();
    setFilters((prev) => ({ ...prev, material_status: '' }));
  };

  // ── Aggregate per-building rows into one row per project per month ──────────
  const projectAggregatedRows = useMemo(() => {
    const dataRows = rows.filter(r => !r._is_group && !r._is_subtotal && !r._is_footer);
    const map = new Map();

    dataRows.forEach(row => {
      const key = `${row.release_month}||${row.project_name}||${row.job_no}`;
      if (!map.has(key)) {
        map.set(key, {
          ...row,
          building_name:      null,
          expected_finish:    row.expected_finish,
          fabrication_chance: row.fabrication_chance,
          mff:         0,
          cold_formed: 0,
          sheeting:    0,
          steel_plate: Object.fromEntries(steelSpecs.map(s => [s, 0])),
          square_tube: { ...Object.fromEntries(tubeSpecs.map(s => [s, 0])), TOTAL: 0 },
          hgi:         { ...Object.fromEntries(hgiSpecs.map(s => [s, 0])), TOTAL: 0 },
          _buildingRows: [],
        });
      }
      const agg = map.get(key);
      agg._buildingRows.push(row);
      agg.mff         += row.mff         || 0;
      agg.cold_formed += row.cold_formed || 0;
      agg.sheeting    += row.sheeting    || 0;
      steelSpecs.forEach(s => { agg.steel_plate[s]  += row.steel_plate?.[s]  || 0; });
      tubeSpecs.forEach(s  => { agg.square_tube[s]  += row.square_tube?.[s]  || 0; });
      agg.square_tube.TOTAL += row.square_tube?.TOTAL || 0;
      hgiSpecs.forEach(s   => { agg.hgi[s]          += row.hgi?.[s]          || 0; });
      agg.hgi.TOTAL += row.hgi?.TOTAL || 0;
    });

    // Release date of a project row = earliest release date of its buildings in the month.
    return Array.from(map.values()).map(({ _buildingRows, ...agg }) => ({
      ...agg,
      expected_finish: earliestReleaseLabel(_buildingRows),
    }));
  }, [rows, steelSpecs, tubeSpecs, hgiSpecs]);

  // Source rows depends on current view mode — no extra API call needed
  const sourceRows = viewMode === 'project' ? projectAggregatedRows : rows;

  // ── Group rows by release_month with subtotals ─────────────────────────────
  const groupedRows = useMemo(() => {
    if (!sourceRows.length) return [];

    const dataRows = sourceRows.filter(r => !r._is_group && !r._is_footer && !r._is_subtotal);

    const grouped = {};
    dataRows.forEach(row => {
      const month = row.release_month || 'Unknown';
      if (!grouped[month]) grouped[month] = [];
      grouped[month].push(row);
    });

    const sortedMonths = Object.keys(grouped).sort((a, b) => {
      try {
        return dayjs(a, 'MMM-YY').toDate().getTime() - dayjs(b, 'MMM-YY').toDate().getTime();
      } catch {
        return a.localeCompare(b);
      }
    });

    const result = [];
    let seq = 1;
    sortedMonths.forEach(month => {
      const monthRows = grouped[month];
      const uniqueProjects = new Set(monthRows.map(r => r.project_name)).size;

      const monthSubtotals = {
        _is_group:          true,
        _group_level:       1,
        job_name:           `📅 ${month}`,
        project_count:      uniqueProjects,
        job_no:             null,
        project_name:       '',
        building_name:      '',
        expected_finish:    '',
        fabrication_chance: '',
        mff:         monthRows.reduce((s, r) => s + (r.mff         || 0), 0),
        cold_formed: monthRows.reduce((s, r) => s + (r.cold_formed || 0), 0),
        sheeting:    monthRows.reduce((s, r) => s + (r.sheeting    || 0), 0),
        steel_plate: {},
        square_tube: {},
        hgi:         {}
      };

      steelSpecs.forEach(spec => {
        monthSubtotals.steel_plate[spec] = monthRows.reduce((s, r) => s + (r.steel_plate?.[spec] || 0), 0);
      });
      tubeSpecs.forEach(spec => {
        monthSubtotals.square_tube[spec] = monthRows.reduce((s, r) => s + (r.square_tube?.[spec] || 0), 0);
      });
      hgiSpecs.forEach(spec => {
        monthSubtotals.hgi[spec] = monthRows.reduce((s, r) => s + (r.hgi?.[spec] || 0), 0);
      });
      monthSubtotals.square_tube.TOTAL = monthRows.reduce((s, r) => s + (r.square_tube?.TOTAL || 0), 0);
      monthSubtotals.hgi.TOTAL         = monthRows.reduce((s, r) => s + (r.hgi?.TOTAL         || 0), 0);

      result.push(monthSubtotals);
      monthRows.forEach((row) => result.push({ ...row, _seq: seq++ }));
    });

    return result;
  }, [sourceRows, steelSpecs, tubeSpecs, hgiSpecs]);

  // ── Column definitions ─────────────────────────────────────────────────────
  const columnDefs = useMemo(() => [
    {
      headerName: 'Job Information',
      groupId: 'job_info',
      children: [
        {
          headerName: 'No.', field: '_seq', width: 72, pinned: 'left', sortable: false, resizable: false,
          checkboxSelection: (params) => !params.data?._is_group && !params.data?._is_footer,
          headerCheckboxSelection: true,
          headerCheckboxSelectionFilteredOnly: true,
          cellStyle: { textAlign: 'center', color: '#6B7280', fontSize: '11px', fontWeight: 500, paddingLeft: 4 },
          valueGetter: (p) => {
            if (p.data?._is_group)  return '';
            if (p.data?._is_footer) return '∑';
            return p.data?._seq ?? '';
          },
        },
        {
          headerName: 'Job no.',
          field: 'job_no',
          minWidth: 90,
          flex: 1,
          pinned: 'left',
          // Kept so copy/export still yield the job number rather than the markup.
          valueFormatter: (p) => {
            if (p.data?._is_group || p.data?._is_footer) return p.data.job_name || '';
            return p.value || '';
          },
          cellRenderer: (p) => {
            // Group and grand-total rows reuse this cell for their label.
            if (p.data?._is_group || p.data?._is_footer) return p.data?.job_name || '';
            if (!p.data?.project_id) return p.value || '—';

            return (
              <Link
                to={`/crm/projects/${p.data.project_id}`}
                target="_blank"
                className="tw-text-blue-600 tw-font-mono tw-text-xs tw-no-underline hover:tw-underline"
              >
                {p.value || '—'}
              </Link>
            );
          }
        },
        {
          headerName: 'Project name',
          field: 'project_name',
          minWidth: 180,
          flex: 1,
          valueFormatter: (p) => {
            if (p.data?._is_group || p.data?._is_footer) {
              return `${p.data.project_count} project${p.data.project_count !== 1 ? 's' : ''}`;
            }
            return p.value || '';
          }
        },
        {
          headerName: 'Building',
          field: 'building_name',
          minWidth: 100,
          flex: 2,
          hide: viewMode === 'project',
          valueFormatter: (p) => {
            if (p.data?._is_group || p.data?._is_footer) return '';
            return p.value || '';
          }
        },
        {
          headerName: 'Release',
          field: 'expected_finish',
          width: 130,
          valueFormatter: (p) => {
            if (p.data?._is_group || p.data?._is_footer) return '';
            return p.value || '';
          }
        },
        {
          headerName: 'Material status',
          field: 'material_request_status',
          width: 125,
          cellRenderer: (params) => {
            if (params.data?._is_group || params.data?._is_footer) return '';
            const status = params.value || 'not_requested';
            return (
              <MaterialStatusBadge
                status={status}
                label={params.data?.material_status_label}
                progressLabel={params.data?.material_progress_label}
              />
            );
          },
        },
        {
          headerName: 'Fabrication/ Chance',
          field: 'fabrication_chance',
          width: 90,
          type: 'numericColumn',

          // PlanningService#format_percentage already returns "80%" (and '' when
          // blank) — the same string the Excel export writes. Appending another
          // '%' here rendered "80%%".
          valueFormatter: (p) => {
            if (p.data?._is_group || p.data?._is_footer) return '';
            return p.value ?? '';
          }
        },
      ],
    },

    {
      headerName: 'Metric Tones',
      groupId: 'metric',
      headerClass: 'tw-bg-amber-300',
      children: [
        {
          headerName: 'MFF',
          headerClass: 'tw-bg-amber-200',
          cellClass: 'tw-bg-amber-100',
          field: 'mff',
          width: 80,
          type: 'numericColumn',
          valueFormatter: (p) => p.value != null ? formatNumber(p.value) : ''
        },
        {
          headerName: 'CF',
          headerClass: 'tw-bg-amber-200',
          cellClass: 'tw-bg-amber-100',
          field: 'cold_formed',
          width: 80,
          type: 'numericColumn',
          valueFormatter: (p) => p.value != null ? formatNumber(p.value) : ''
        },
        {
          headerName: 'SH',
          headerClass: 'tw-bg-amber-200',
          cellClass: 'tw-bg-amber-100',
          field: 'sheeting',
          width: 80,
          type: 'numericColumn',
          valueFormatter: (p) => p.value != null ? formatNumber(p.value) : ''
        },
      ],
    },

    {
      headerName: 'STEEL PLATE (Kg)',
      groupId: 'steel_plate',
      headerClass: 'tw-bg-blue-300',
      children: steelSpecs.map((spec) => ({
        headerName: spec,
        headerClass: 'tw-bg-blue-200',
        cellClass: 'tw-bg-blue-100',
        suppressMenu: true,
        field: `sp_${spec}`,
        width: 80,
        type: 'numericColumn',
        valueGetter: (p) => p.data?.steel_plate?.[spec] ?? 0,
        valueFormatter: (p) => p.value != null ? formatNumber(p.value) : ''
      })),
    },

    {
      headerName: 'Square Tube',
      groupId: 'square_tube',
      headerClass: 'tw-bg-green-300',
      children: [
        ...tubeSpecs.map((spec) => ({
          headerName: spec,
          headerClass: 'tw-bg-green-200',
          cellClass: 'tw-bg-green-100',
          suppressMenu: true,
          field: `tb_${spec}`,
          width: 110,
          type: 'numericColumn',
          valueGetter: (p) => p.data?.square_tube?.[spec] ?? 0,
          valueFormatter: (p) => p.value != null ? formatNumber(p.value) : ''
        })),
        {
          headerName: 'TOTAL',
          headerClass: 'tw-bg-green-200',
          cellClass: 'tw-bg-green-100',
          suppressMenu: true,
          field: 'tb_total',
          width: 110,
          type: 'numericColumn',
          valueGetter: (p) => p.data?.square_tube?.TOTAL ?? 0,
          valueFormatter: (p) => p.value != null ? formatNumber(p.value) : '',
          cellStyle: { fontWeight: 'bold' },
        },
      ],
    },

    {
      headerName: 'HGI (Kg)',
      groupId: 'hgi',
      headerClass: 'tw-bg-orange-300',
      children: [
        ...hgiSpecs.map((spec) => ({
          headerName: spec,
          headerClass: 'tw-bg-orange-200',
          cellClass: 'tw-bg-orange-100',
          suppressMenu: true,
          field: `hg_${spec}`,
          width: 110,
          type: 'numericColumn',
          valueGetter: (p) => p.data?.hgi?.[spec] ?? 0,
          valueFormatter: (p) => p.value != null ? formatNumber(p.value) : ''
        })),
        {
          headerName: 'TOTAL',
          headerClass: 'tw-bg-orange-200',
          cellClass: 'tw-bg-orange-100',
          suppressMenu: true,
          field: 'hg_total',
          width: 110,
          type: 'numericColumn',
          valueGetter: (p) => p.data?.hgi?.TOTAL ?? 0,
          valueFormatter: (p) => p.value != null ? formatNumber(p.value) : '',
          cellStyle: { fontWeight: 'bold' },
        },
      ],
    },
  ], [steelSpecs, tubeSpecs, hgiSpecs, viewMode]);

  const defaultColDef = useMemo(() => ({
    resizable: true,
    sortable: false,
    suppressMovable: false,
    wrapHeaderText: false,
    autoHeaderHeight: true,
  }), []);

  const getRowStyle = useCallback((params) => {
    if (params.data?._is_group) {
      return {
        background: '#d6e4f0',
        fontWeight: 'bold',
        color: '#1f3864',
        borderBottom: '1px solid #94a3b8'
      };
    }
    if (params.data?._is_footer || params.node?.rowPinned === 'bottom') {
      return { background: '#FFF2CC', fontWeight: 'bold', borderTop: '2px solid #d97706' };
    }
    return null;
  }, []);

  const onFirstDataRendered = useCallback(() => {
    if (gridRef.current?.api) {
      gridRef.current.api.autoSizeAllColumns();
    }
  }, []);

  const totalPages = Math.ceil(totalRows / perPage);

  // Footer totals from server (pre-calculated for all records, avoiding pagination)
  const footerTotals = useMemo(() => {
    return reportState.allData?.grand_totals || {
      project_count: totalProjects,
      mff: 0,
      cold_formed: 0,
      sheeting: 0,
      steel_plate: {},
      square_tube: {},
      hgi: {}
    };
  }, [reportState.allData?.grand_totals, totalProjects]);

  const pinnedBottomRowData = [{
    _is_footer:    true,
    job_name:      '📌 GRAND TOTAL',
    project_count: footerTotals.project_count,
    job_no:        null,
    mff:           footerTotals.mff,
    cold_formed:   footerTotals.cold_formed,
    sheeting:      footerTotals.sheeting,
    steel_plate:   footerTotals.steel_plate,
    square_tube:   footerTotals.square_tube,
    hgi:           footerTotals.hgi
  }];

  const handleReleaseToProduction = useCallback(() => {
    setProductionReleaseTarget({
      period: filters.period,
      recordCount: pendingProjects.length || unsentCount,
      projects: pendingProjects,
      // Rows are paginated while the release covers the whole period.
      partialList: totalRows > rows.length
    });
  }, [filters.period, unsentCount, pendingProjects, totalRows, rows.length]);

  const handleProductionReleaseConfirm = useCallback(
    (period, email) => {
      if (!period) {
        toast.error('Period is required');
        return;
      }

      return dispatch(releasePlanningToProduction({ period, ...email }))
        .unwrap()
        .then((result) => {
          toast.success(`✅ Released ${result.record_count} record(s) to production for period ${period}`);
          setProductionReleaseTarget(null);
          reload(1);
        })
        .catch((error) => {
          toast.error(error?.message || `❌ Failed to release records for period ${period}`);
        });
    },
    [dispatch, reload]
  );

  const handleExportExcel = useCallback(() => {
    dispatch(exportPlanningForecast({ ...buildParams(), view_mode: viewMode }));
  }, [dispatch, buildParams, viewMode]);

  const handleSelectionChanged = useCallback((event) => {
    setSelectedCsdIds([...new Set(event.api.getSelectedRows().map((row) => row.csd_id).filter(Boolean))]);
  }, []);

  const handleMaterialRequest = useCallback((email) => {
    dispatch(requestPlanningForecastMaterials({ period: filters.period, csd_ids: selectedCsdIds, ...email })).unwrap()
      .then((result) => {
        toast.success(`Tracked ${result.record_count} project(s): ${result.released_count || 0} released, ${result.partial_count || 0} partial, ${result.requested_count || 0} pending`);
        setMaterialRequestOpen(false);
        setSelectedCsdIds([]);
        reload(1);
      })
      .catch((error) => toast.error(error?.error || error?.message || 'Request failed'));
  }, [dispatch, filters.period, selectedCsdIds, reload]);

  // Recalculation overwrites weights in place, so it asks first — the other two
  // bulk actions on this toolbar already open their own confirmation dialog.
  const handleRecalculateDocuments = useCallback(() => {
    const projectNames = [...new Set(
      rows.filter((row) => selectedCsdIds.includes(row.csd_id))
          .map((row) => row.project_name)
          .filter(Boolean)
    )];
    // confirmAlert renders its message as HTML — project names must be escaped.
    const preview = projectNames.slice(0, 3).map(escapeHtml).join(', ');
    const remaining = projectNames.length - 3;
    const named = preview
      ? ` (${preview}${remaining > 0 ? ` +${remaining} more` : ''})`
      : '';

    confirmAlert({
      title: 'Recalculate from documents',
      message:
        `Recalculate weights for <strong>${selectedCsdIds.length} project(s)</strong>${named} ` +
        'from the released Forecast Material documents?' +
        '<br><br>Current weights will be overwritten. ' +
        'Forecasts already released to Production are skipped.',
      closeOnEscape: true,
      closeOnClickOutside: false,
      buttons: [
        {
          label: 'Recalculate',
          variant: 'primary',
          onClick: () => {
            dispatch(recalculatePlanningFromDocuments({ csd_ids: selectedCsdIds })).unwrap()
              .then(() => { toast.success('Forecast recalculated from released documents'); reload(1); })
              .catch((error) => toast.error(error?.error || error?.message || 'Recalculate failed'));
          },
        },
        { label: 'Cancel', onClick: () => {} },
      ],
    });
  }, [dispatch, selectedCsdIds, rows, reload]);

  useEffect(() => { setPageReady(true); }, []);

  if (!pageReady) {
    return (
      <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-min-h-screen tw-bg-gray-50 tw-gap-4">
        <LoadingSpinner size="lg" />
        <p className="tw-text-sm tw-text-gray-500 tw-animate-pulse">Loading Release Forecast Planning…</p>
      </div>
    );
  }

  return (
    <div className="tw-flex tw-flex-col tw-h-full tw-gap-3 tw-p-4">
      {/* Header */}
      <div className="tw-mb-2">
        <div className="tw-flex tw-items-center tw-gap-3 tw-mb-1">
          <div className="tw-w-1.5 tw-h-10" style={{ background: 'linear-gradient(to bottom, #4f46e5, #818cf8)', borderRadius: 3 }} />
          <div>
            <h1 className="tw-mb-0 tw-font-bold tw-text-2xl tw-text-gray-900">3-Months Production Forecast</h1>
          </div>
        </div>
        <p className="tw-text-gray-600 tw-text-sm tw-ml-7">Based on CSD released forecast — comprehensive 3-month building production plan</p>
      </div>

      {/* Filter + Action bar */}
      <div className="tw-flex tw-flex-wrap tw-items-end tw-gap-1 tw-bg-white tw-rounded tw-p-3 tw-shadow-sm tw-border tw-border-slate-200">
        <div>
          <label className="tw-text-xs tw-font-semibold tw-text-gray-500 tw-mb-1 tw-block">Period</label>
          <MonthYearPicker
            value={filters.period}
            onChange={(v) => setFilter('period', v)}
          />
        </div>

        <div>
          <label className="tw-text-xs tw-font-semibold tw-text-gray-500 tw-mb-1 tw-block">Project / Job</label>
          <div className="tw-relative">
            <FaSearch size={10} className="tw-absolute tw-left-2.5 tw-top-1/2 -tw-translate-y-1/2 tw-text-gray-400 tw-pointer-events-none" />
            <input
              type="text"
              placeholder="Search project or job number…"
              className="tw-h-9 tw-border tw-border-slate-300 tw-rounded tw-pl-7 tw-pr-3 tw-text-xs tw-w-64 tw-outline-none focus:tw-border-primary-500 focus:tw-ring-1 focus:tw-ring-primary-200"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
          </div>
        </div>

        <div>
          <label className="tw-text-xs tw-font-semibold tw-text-gray-500 tw-mb-1 tw-block">Material Status</label>
          <MaterialStatusSelect
            value={filters.material_status}
            onChange={(value) => setFilter('material_status', value)}
          />
        </div>

        {hasFilters && (
          <Button
            variant="secondary"
            size="sm"
            onClick={clearFilters}
            className="tw-text-slate-600 tw-border-slate-200 hover:tw-bg-slate-50"
          >
            <FaTimes size={10} /> Clear
          </Button>
        )}

        {/* View mode toggle */}
        <div className="tw-ml-auto tw-flex tw-flex-col tw-justify-end">
          <label className="tw-text-xs tw-font-semibold tw-text-gray-700 tw-mb-2 tw-block">View Mode</label>
          <div className="tw-flex tw-items-center tw-gap-3 tw-bg-gradient-to-r tw-from-slate-100 tw-to-slate-50 tw-rounded tw-p-2 tw-border tw-border-slate-200 tw-shadow-sm hover:tw-shadow-md tw-transition-shadow">
            <span className={`tw-text-xs tw-font-medium tw-px-2 tw-py-1 tw-rounded-sm tw-transition-all ${
              viewMode === 'project' 
                ? 'tw-text-primary-700 tw-bg-primary-100/60' 
                : 'tw-text-gray-600'
            }`}>
              <MdAssignment size={16} />
              Project
            </span>
            <button
              type="button"
              onClick={() => setViewMode(viewMode === 'project' ? 'building' : 'project')}
              className={`tw-relative tw-w-11 tw-h-6 tw-rounded-xl tw-transition-all tw-duration-300 tw-flex tw-items-center tw-shadow-inner focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-primary-400 focus:tw-ring-offset-1 ${
                viewMode === 'building' 
                  ? 'tw-bg-gradient-to-r tw-from-primary-500 tw-to-primary-600' 
                  : 'tw-bg-gradient-to-r tw-from-gray-300 tw-to-gray-400'
              }`}
            >
              <span className={`tw-absolute tw-w-5 tw-h-5 tw-bg-white tw-rounded-xl tw-shadow-lg
                tw-transition-transform tw-duration-300 tw-left-0.5 ${
                viewMode === 'building' ? 'tw-translate-x-5' : 'tw-translate-x-0'
              }`}></span>
            </button>
            <span className={`tw-text-xs tw-font-medium tw-px-2 tw-py-1 tw-rounded-sm tw-transition-all ${
              viewMode === 'building' 
                ? 'tw-text-primary-700 tw-bg-primary-100/60' 
                : 'tw-text-gray-600'
            }`}>
              <MdApartment size={16} />
              Building
            </span>
          </div>
        </div>

        <PlanningToolbarActions
          loading={loading}
          exporting={exportLoading}
          onExport={handleExportExcel}
          selectedCount={selectedCsdIds.length}
          onRequestMaterials={() => setMaterialRequestOpen(true)}
          onRecalculateDocuments={handleRecalculateDocuments}
          unsentCount={unsentCount}
          onReleaseToProduction={handleReleaseToProduction}
        />
      </div>

      {/* Summary cards */}
      {rows.length > 0 && (
        <div className="tw-rounded-lg tw-border tw-border-gray-200 tw-shadow-md tw-bg-white tw-overflow-hidden">
          <div className="tw-px-6 tw-py-4 tw-bg-gradient-to-r tw-from-primary-50 tw-to-blue-50 tw-border-b tw-border-gray-200">
            <h3 className="tw-text-xs tw-font-bold tw-text-gray-900 tw-uppercase tw-tracking-wide tw-flex tw-items-center tw-gap-2 tw-mb-3">
              <span className="tw-w-1 tw-h-4 tw-bg-primary-500 tw-rounded-xl" />
              Summary (All Records)
            </h3>
            <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 lg:tw-grid-cols-5 tw-gap-4">
              {/* Total Records */}
              <div className="tw-rounded tw-bg-white tw-border tw-border-blue-100 tw-p-4 tw-shadow-sm hover:tw-shadow-md tw-transition-shadow">
                <div className="tw-flex tw-items-center tw-justify-between">
                  <div>
                    <div className="tw-text-xs tw-font-semibold tw-text-gray-600 tw-uppercase tw-tracking-wide tw-mb-1">Total projects</div>
                    <div className="tw-font-bold tw-text-xl tw-text-primary-600">{footerTotals.project_count}</div>
                    <div className="tw-text-xs tw-text-gray-500 tw-mt-1">{footerTotals.project_count} project{footerTotals.project_count !== 1 ? 's' : ''}</div>
                  </div>
                  <div className="tw-text-3xl tw-text-blue-200"><MdNumbers /></div>
                </div>
              </div>

              {/* Grand Total */}
              <div className="tw-rounded tw-bg-white tw-border tw-border-green-100 tw-p-4 tw-shadow-sm hover:tw-shadow-md tw-transition-shadow">
                <div className="tw-flex tw-items-center tw-justify-between">
                  <div>
                    <div className="tw-text-xs tw-font-semibold tw-text-gray-600 tw-uppercase tw-tracking-wide tw-mb-1">Grand Total</div>
                    <div className="tw-font-bold tw-text-xl tw-text-green-700">{formatNumber(footerTotals.mff + footerTotals.cold_formed + footerTotals.sheeting)}</div>
                    <div className="tw-text-xs tw-text-green-600 tw-font-semibold tw-mt-1">MT</div>
                  </div>
                  <div className="tw-text-3xl tw-text-green-200"><MdScale /></div>
                </div>
              </div>

              <div className="tw-rounded tw-bg-white tw-border tw-border-green-100 tw-p-4 tw-shadow-sm hover:tw-shadow-md tw-transition-shadow">
                <div className="tw-flex tw-items-center tw-justify-between">
                  <div>
                    <div className="tw-text-xs tw-font-semibold tw-text-gray-600 tw-uppercase tw-tracking-wide tw-mb-1">MFF</div>
                    <div className="tw-font-bold tw-text-xl tw-text-blue-700">{formatNumber(footerTotals.mff)}</div>
                    <div className="tw-text-xs tw-text-blue-600 tw-font-semibold tw-mt-1">MT</div>
                  </div>
                  <div className="tw-text-3xl tw-text-blue-200"><MdScale /></div>
                </div>
              </div>

              <div className="tw-rounded tw-bg-white tw-border tw-border-green-100 tw-p-4 tw-shadow-sm hover:tw-shadow-md tw-transition-shadow">
                <div className="tw-flex tw-items-center tw-justify-between">
                  <div>
                    <div className="tw-text-xs tw-font-semibold tw-text-gray-600 tw-uppercase tw-tracking-wide tw-mb-1">Cold Formed</div>
                    <div className="tw-font-bold tw-text-xl tw-text-blue-700">{formatNumber(footerTotals.cold_formed)}</div>
                    <div className="tw-text-xs tw-text-blue-600 tw-font-semibold tw-mt-1">MT</div>
                  </div>
                  <div className="tw-text-3xl tw-text-blue-200"><MdScale /></div>
                </div>
              </div>

              <div className="tw-rounded tw-bg-white tw-border tw-border-green-100 tw-p-4 tw-shadow-sm hover:tw-shadow-md tw-transition-shadow">
                <div className="tw-flex tw-items-center tw-justify-between">
                  <div>
                    <div className="tw-text-xs tw-font-semibold tw-text-gray-600 tw-uppercase tw-tracking-wide tw-mb-1">Sheeting</div>
                    <div className="tw-font-bold tw-text-xl tw-text-blue-700">{formatNumber(footerTotals.sheeting)}</div>
                    <div className="tw-text-xs tw-text-blue-600 tw-font-semibold tw-mt-1">MT</div>
                  </div>
                  <div className="tw-text-3xl tw-text-blue-200"><MdScale /></div>
                </div>
              </div>
 
            </div>
          </div>
        </div>
      )}

      {/* Grid */}
      <div className="tw-relative" style={{ width: '100%', height: 600, '--ag-font-size': '12px' }}>
        {loading && (
          <div className="tw-absolute tw-inset-0 tw-flex tw-items-center tw-justify-center tw-bg-white/60 tw-z-10">
            <LoadingSpinner />
          </div>
        )}
        {exportLoading && (
          <LoadingSpinner fullScreen message="Exporting Excel..." />
        )}
        <AgGridReact
          key={`${steelSpecs.length}-${tubeSpecs.length}-${hgiSpecs.length}-${viewMode}`}
          ref={gridRef}
          rowData={groupedRows}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          getRowStyle={getRowStyle}
          onFirstDataRendered={onFirstDataRendered}
          rowSelection="multiple"
          onSelectionChanged={handleSelectionChanged}
          isRowSelectable={(params) => !params.data?._is_group && !params.data?._is_footer}
          suppressPaginationPanel
          rowHeight={26}
          getRowHeight={(params) => {
            if (params.data?._is_group || params.data?._is_footer) return 36;
            return 26;
          }}
          headerHeight={36}
          groupHeaderHeight={28}
          columnGroupHeaderHeight={32}
          suppressColumnVirtualisation={false}
          pinnedBottomRowData={pinnedBottomRowData}
        />
      </div>

      {/* Pagination */}
      <div className="tw-flex tw-items-center tw-justify-end tw-bg-slate-50 tw-rounded tw-p-3 tw-border tw-border-slate-200">
        <Pagination
          currentPage={page}
          totalPages={totalPages}
          onPageChange={(newPage) => dispatch(setPlanningReportPage(newPage))}
          onPageSizeChange={(newSize) => dispatch(setPlanningReportPageSize(newSize))}
          totalResults={totalRows}
          perPage={perPage}
          disabled={loading}
          compact={true}
          pageSizeOptions={[20, 50, 100, 200]}
        />
      </div>

      {/* Production Release Dialog */}
      {productionReleaseTarget && (
        <ProductionReleaseConfirmDialog
          record={productionReleaseTarget}
          defaultPeriod={filters.period}
          onConfirm={handleProductionReleaseConfirm}
          onCancel={() => setProductionReleaseTarget(null)}
        />
      )}
      {materialRequestOpen && (
        <MaterialRequestDialog
          period={filters.period}
          count={selectedCsdIds.length}
          onConfirm={handleMaterialRequest}
          onCancel={() => setMaterialRequestOpen(false)}
        />
      )}
    </div>
  );
};

export default ReleaseForecastPlanningPage;
